/* eslint-disable import/prefer-default-export */
import { casingFormat } from 'common/utils';
import { format, isValid, parseISO } from 'date-fns';

/* Only for FILL_LATER_RESCHEDULED, whose payload carries the day as a UTC-midnight TIMESTAMP
 * (fillLaterActionService sends toIso(...) / toISOString()). Formatting that instant in the
 * browser's zone shows the previous day to anyone west of UTC, so the UTC calendar parts are read
 * first. MM-dd-yyyy matches the Fill Later work queue.
 *
 * Do not point this at a date-only string ("2026-09-20"): parseISO reads that as LOCAL midnight,
 * so east of UTC the UTC parts come back a day early. That is exactly what FILL_LATER_SCHEDULED
 * did before its dates were dropped. */
const formatFillLaterDay = (value) => {
  const parsed = typeof value === 'string' ? parseISO(value) : value;
  if (!parsed || !isValid(parsed)) return '';
  return format(
    new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate()),
    'MM-dd-yyyy'
  );
};

const countFillLaterSplits = (values) => (Array.isArray(values) ? values.length : 0);

export const getEventDescription = (requestData, eventData) => {
  const firstName = casingFormat(requestData.firstName) || '';
  const lastName = casingFormat(requestData.lastName) || '';
  const actionItemType = requestData.actionItemType || '';
  const orderId = eventData?.orderId || requestData.orderId || '';
  const requestId = requestData.requestId || '';
  const orderShipmentId = requestData.orderShipmentId || '';
  const rxNumber = requestData.rxNumber || '';
  const referenceId = requestData.referenceId || '';
  const fromPharmacyNpi = requestData.fromPharmacyNpi || '';
  const toPharmacyNpi = requestData.toPharmacyNpi || '';
  const transferPharmacy = eventData?.transferPharmacy || requestData.transferPharmacy || '';
  const transferPharmacyName =
    eventData?.transferPharmacyName || requestData.transferPharmacyName || '';
  const failureReason = eventData?.failureReason || requestData.failureReason || '';
  const patientEmail = eventData?.patientEmail || '';
  const lineItemId = requestData.lineItemId || eventData?.[0]?.lineItemId || '';
  const previousProductId = eventData?.previousProductId || '';
  const overriddenProductId = eventData?.productId || '';
  /* Numbers, so `||` would swallow a legitimate 0 and a nullish check is not enough on its own --
   * the row carries no previousQuantity at all when the line item had no override before. */
  const overriddenQuantity = eventData?.quantity ?? '';
  const previousQuantity = eventData?.previousQuantity ?? '';
  /* Fill Later (POM-6150). Both events carry the same auditEventData and differ only in which
   * order they are attached to, so the new order id is read explicitly rather than from
   * `orderId` -- on the original order's event `orderId` is the ORIGINAL, and the description
   * has to name the order that was just created either way. */
  const splitOrderId = eventData?.splitOrderId || '';
  const splitFromOrderId = eventData?.referenceOrderId || '';
  /* A quantity, so `??` for the same reason as previousQuantity above. */
  const splitQuantity = eventData?.splitQuantity ?? '';
  const splitRxNumber = eventData?.rxNumber || rxNumber || '';
  /* Fill Later interaction-history events (FILL_LATER_SCHEDULED / RESCHEDULED / MERGED). The
   * payload is the ESIH `payload` object core posts; quantities are formatted strings and can be
   * "0", so `??` keeps them. */
  const originalQuantity = eventData?.originalQuantity ?? '';
  const retainedQuantity = eventData?.retainedQuantity ?? '';
  /* previousQuantity is declared above (shared with QUANTITY_OVERRIDDEN). */
  const restoredQuantity = eventData?.restoredQuantity ?? '';
  const scheduledSplitCount = countFillLaterSplits(eventData?.scheduledFor);
  /* FILL_LATER_ORDER_MOVED carries the fill date as a date-only string, so it is shown as stored
   * rather than put through formatFillLaterDay -- parsing "2026-10-14" as a Date is exactly the
   * off-by-one the helper above warns about. */
  const movedItemCount = Array.isArray(eventData?.lineItemIds) ? eventData.lineItemIds.length : 0;
  const movedFillDate = typeof eventData?.scheduledFor === 'string' ? eventData.scheduledFor : '';
  const previousScheduledDay = formatFillLaterDay(eventData?.previousScheduledFor);
  const newScheduledDay = formatFillLaterDay(eventData?.scheduledFor);
  const rxLabel = `Rx ${splitRxNumber || 'NA'}`;
  const heldOrderId = eventData?.heldOrderId || '';
  const heldFromOrderId = eventData?.sourceOrderId || '';
  const movedQuantity = eventData?.movedQuantity ?? '';
  const isNewHeldOrder = eventData?.holdMode === 'NEW_ORDER';
  const releasedOrderOpened = eventData?.orderOpened === true;
  const stillHeldCount = Array.isArray(eventData?.stillHeldItemIds)
    ? eventData.stillHeldItemIds.length
    : 0;
  const resolvedOrderId = Array.isArray(eventData?.orderId)
    ? eventData.orderId[0]
    : eventData?.orderId || requestData.orderId || '';

  const actionItemTypeHandler = {
    patientPrescreening: `Patient ${lastName},${firstName} prescreening has completed`,
    LABEL_PRINTED: `${orderId} - Order's labels printed`,
    PRODUCT_FULFILLED: `${lastName}, ${firstName}'s order ${orderId} with Rx number ${
      rxNumber || 'NA'
    } has fulfilled`,
    PRODUCT_FULFILLMENT_UNDONE: `${lastName}, ${firstName}'s order ${orderId} with Rx number ${
      rxNumber || 'NA'
    } fulfillment undone`,
    CREATED_NEW_ORDER: `New order ${orderId} created for patient ${lastName}, ${firstName}`,
    NEW_REQUEST_CREATED: `New Request ${requestId} created for patient ${lastName}, ${firstName}`,
    SHIPSTATION_ORDER_CREATED: `Shipstation order ${orderShipmentId} has been created for the order ${orderId}`,
    ORDER_CANCELED: `Order ${orderId} has been canceled`,
    LINE_ITEM_CANCELED: 'Order line item canceled',
    SHIPSTATION_ORDER_SHIPPED: `Shipstation order ${orderShipmentId} has been shipped successfully`,
    PRESCRIPTION_DIRECTION_REVIEW: `Prescription ${rxNumber} has marked for prescription direction review`,
    PRESCRIPTION_DIRECTION_REVIEW_PASS: `Prescription direction review passed for prescription ${rxNumber}`,
    ORDER_MARKED_FOR_REVIEW: `Order ${orderId} marked for external processing`,
    PRESCRIPTION_DUPLICATE_REVIEW: `Prescription ${rxNumber} has marked for prescription duplicate review`,
    PRESCRIPTION_DUPLICATE_REVIEW_PASS: `Prescription duplicate review passed for prescription ${rxNumber}`,
    DRUG_PROFILE_REVIEW: `Prescription ${rxNumber} is marked for drug review as a matching drug profile was not found`,
    DRUG_PROFILE_REVIEW_PASS: `Drug review completed successfully for line item ${lineItemId}. The prescription passed the drug review and is ready for the next workflow step`,
    DRUG_REVIEW_PASSED: `Drug review passed${rxNumber ? ` for prescription ${rxNumber}` : ''}`,
    LINE_ITEM_DRUG_PROFILE_RE_PROCESS: `Order ${orderId} line item was marked for automation of drug review`,
    ORDER_CREATED_IN_DYNAMO: `Prescription input validation has passed and the same is saved in Dynamo DB with reference Id: ${referenceId}`,
    PRESCRIPTION_REVIEW: `Prescription ${rxNumber} is marked for Prescription review`,
    MANUAL_ORDER_REVIEW: `Order${orderId ? ` ${orderId}` : ''} is marked for manual order review`,
    MULTI_RX_REVIEW: `Prescription${rxNumber ? ` ${rxNumber}` : ''} is marked for multi-Rx review`,
    OTHER_REVIEW: `Order${orderId ? ` ${orderId}` : ''} is marked for other review`,
    PROVIDER_CLARIFICATION_REVIEW: `Prescription${
      rxNumber ? ` ${rxNumber}` : ''
    } is marked for provider clarification review`,
    SHIPPING_ADDRESS_REVIEW: `Order${orderId ? ` ${orderId}` : ''} is marked for shipping address review`,
    ORDER_VERIFICATION: `Order ${orderId} is ready for verification`,
    ORDER_VERIFIED: `Order ${orderId} is verified by Pharmacist`,
    ORDER_RECEIVED_PARTNER_WEBHOOK_SUCCESS:
      'Order received event webhook was successfully sent to the partner',
    ORDER_SHIPPED_PARTNER_WEBHOOK_SUCCESS:
      'Order shipped event webhook was successfully sent to the partner',
    ORDER_CANCELED_PARTNER_WEBHOOK_SUCCESS:
      'Order canceled event webhook was successfully sent to the partner',
    ORDER_TRANSFERRED_PARTNER_WEBHOOK_SUCCESS:
      'Order transferred event webhook was successfully sent to the partner',
    ORDER_TRANSFERRED_PARTNER_WEBHOOK_FAILURE:
      'Order transferred event webhook failed to send to the partner',
    SHIPMENT_VOIDED_WEBHOOK_SUCCESS:
      'Shipment voided event webhook was successfully sent to the partner',
    ORDER_RECEIVED_PARTNER_WEBHOOK_FAILURE:
      'Order received event webhook failed to send to the partner',
    ORDER_SHIPPED_PARTNER_WEBHOOK_FAILURE:
      'Order shipped event webhook failed to send to the partner',
    ORDER_CANCELED_PARTNER_WEBHOOK_FAILURE:
      'Order canceled event webhook failed to send to the partner',
    SHIPMENT_VOIDED_WEBHOOK_FAILURE: 'Shipment voided event webhook failed to send to the partner',
    ORDER_SHIPMENT_VOIDED_PARTNER_WEBHOOK_SUCCESS:
      'Order shipment voided event webhook was successfully sent to the partner',
    ORDER_SHIPMENT_VOIDED_FOR_ROLLBACK: `Order ${orderId} shipment has been voided for rollback`,
    E_SCRIPT_ORDER_CREATION_FAILED: 'Transmit EScript order creation in POM failed',
    PATIENT_PARTNER_ERROR:
      'Patient and partner are not associated or original partner association for the patient and partner was not enabled',
    PRACTICE_NOT_FOUND:
      'Practice provided in the input was invalid and it failed to create EScript order in POM',
    ORDER_BACK_TO_VERIFICATION: `Order ${orderId} moved back to order reverification`,
    ORDER_SENT_BACK_TO_QUEUE: `Order ${orderId} has been sent back to queue`,
    SHIPSTATION_ORDER_DELETED: `Shipstation order ${orderShipmentId} has been deleted for the order ${orderId}`,
    SHIPSTATION_ORDER_BACK_TO_CREATED: `Order moved back to created in ShipStation`,
    FULFILLING_PHARMACY_TRANSFER: `Request transferred from Pharmacy ${fromPharmacyNpi} to Pharmacy ${toPharmacyNpi}`,
    TRANSFER_ORDER_POSTED: transferPharmacy
      ? `Order ${resolvedOrderId} has been transferred and posted to ${transferPharmacy}`
      : `Order ${resolvedOrderId} has been transferred and posted to external pharmacy`,
    TRANSFER_ORDER_TRANSFERRED: transferPharmacyName
      ? `Order ${resolvedOrderId} has been successfully transferred to ${transferPharmacyName}`
      : `Order ${resolvedOrderId} has been successfully transferred to external pharmacy`,
    TRANSFER_ORDER_FAILED: (() => {
      if (failureReason) {
        return transferPharmacyName
          ? `Order ${resolvedOrderId} transfer to ${transferPharmacyName} failed - ${failureReason}`
          : `Order ${resolvedOrderId} transfer failed - ${failureReason}`;
      }
      return transferPharmacyName
        ? `Order ${resolvedOrderId} transfer to ${transferPharmacyName} failed`
        : `Order ${resolvedOrderId} transfer failed`;
    })(),
    ORDER_SENT_TO_PROCESS_EXTERNAL: `Order ${orderId} marked for external processing`,
    LINE_ITEM_DISASSOCIATED_FROM_AN_ORDER: `Line item disassociated from order ${
      eventData?.orderId || 'NA'
    }`,
    ADDED_LINE_ITEM_TO_ORDER: `Added line item to order ${orderId}`,
    ORDER_VOIDED: `Order ${orderId} has been voided`,
    ORDER_SUBMITTED_FROM_API: `Order submitted from API for patient ${lastName}, ${firstName} with reference Id ${referenceId}`,
    ORDER_SUBMITTED_FROM_PORTAL: `Order submitted from Portal for patient ${lastName}, ${firstName} with reference Id ${referenceId}`,
    ORDER_SUBMITTED_FROM_FILE: `Order submitted from File for patient ${lastName}, ${firstName} with reference Id ${referenceId}`,
    ORDER_SUBMITTED_FROM_POM: `Order submitted from POM for patient ${lastName}, ${firstName} with reference Id ${referenceId}`,
    ORDER_SUBMITTED_FROM_RX_INTAKE: `Order submitted from DOCU for patient ${lastName}, ${firstName} with reference Id ${referenceId}`,
    REQUEST_IN_HOLDING_QUEUE: `Request ${requestId} has been placed in holding queue for review`,
    REQUEST_RESOLVED_FROM_HOLDING_QUEUE: `Request ${requestId} has been resolved and removed from holding queue`,
    RESHIPMENT_ORDER_CREATED: `Reshipment order ${orderId} has been created`,
    FILL_LATER_ORDER_CREATED: `Order ${splitOrderId} has been created for the split of Rx ${
      splitRxNumber || 'NA'
    }${splitQuantity !== '' ? ` with quantity ${splitQuantity}` : ''}${
      splitFromOrderId ? `, split from order ${splitFromOrderId}` : ''
    }`,
    FILL_LATER_SPLIT_CREATED_FROM_ORDER: `Rx ${splitRxNumber || 'NA'} was split${
      splitQuantity !== '' ? ` and the remaining quantity ${splitQuantity}` : ' and the remainder'
    } has been moved to the new order ${splitOrderId}`,
    /* The fill dates are deliberately NOT repeated here. They are on the event payload, on the
     * Fill Later queue and on the line item's own chips, all read from the stored row; the
     * description would be the only place restating them, and the only place that could
     * disagree. It did: core posts this payload's scheduledFor as a date-only string while every
     * other consumer gets a UTC timestamp, so the same day rendered a day earlier here than in
     * the queue. What the description is for is which Rx was split and by how much. */
    FILL_LATER_SCHEDULED: (() => {
      const quantityPart =
        originalQuantity !== '' && retainedQuantity !== ''
          ? `${rxLabel} quantity reduced from ${originalQuantity} to ${retainedQuantity}`
          : `Fill Later scheduled for ${rxLabel}`;
      if (scheduledSplitCount === 0) return quantityPart;
      return `${quantityPart}; ${scheduledSplitCount} Fill Later split${
        scheduledSplitCount === 1 ? '' : 's'
      } scheduled`;
    })(),
    FILL_LATER_RESCHEDULED: `Fill Later item rescheduled${
      previousScheduledDay ? ` from ${previousScheduledDay}` : ''
    }${newScheduledDay ? ` to ${newScheduledDay}` : ''}${
      eventData?.previousStatus === 'WAITING_SHIPMENT' ? '; status back to Scheduled' : ''
    }`,
    FILL_LATER_MERGED: `Fill Later quantity${
      splitQuantity !== '' ? ` ${splitQuantity}` : ''
    } merged back into ${rxLabel}${
      previousQuantity !== '' && restoredQuantity !== ''
        ? ` (dispensed quantity ${previousQuantity} → ${restoredQuantity})`
        : ''
    }`,
    /* POM-6180: the whole order parked by the Move the Entire Order action. The row carries no Rx
     * numbers -- it is one event for the order, not per item -- so it counts what moved and names
     * the order, which is what the timeline is missing without it. */
    FILL_LATER_ORDER_MOVED: `Order ${orderId} moved to Fill Later${
      movedItemCount ? ` with ${movedItemCount} line item${movedItemCount === 1 ? '' : 's'}` : ''
    }, parked until ${movedFillDate || 'its scheduled date'}`,
    /* POM-6199: the only row the order an item LEFT can match -- the hold and the schedule are
     * filed on the new order. Written from the source order's point of view for that reason. */
    FILL_LATER_ITEM_MOVED_FROM_ORDER: `${rxLabel} moved to Fill Later on order ${
      heldOrderId || 'NA'
    }${movedQuantity !== '' ? `, quantity ${movedQuantity}` : ''}${
      newScheduledDay ? `, held until ${newScheduledDay}` : ''
    }`,
    // POM-6261: the schedule was called off because its line item was cancelled. The set-aside
    // quantity deliberately does not come back, so the description says so rather than leaving
    // the reader to assume it behaved like a merge.
    FILL_LATER_SCHEDULE_CANCELLED: `Fill Later cancelled for ${rxLabel}${
      newScheduledDay ? `, was scheduled for ${newScheduledDay}` : ''
    }${splitQuantity !== '' ? `; quantity ${splitQuantity} not returned to the prescription` : ''}`,
    FILL_LATER_ORDER_HELD: isNewHeldOrder
      ? `${rxLabel} moved to new order ${heldOrderId}${
          heldFromOrderId ? ` from order ${heldFromOrderId}` : ''
        }, held until ${newScheduledDay || 'its scheduled date'}`
      : `Order ${heldOrderId} held for ${rxLabel} until ${newScheduledDay || 'its scheduled date'}`,
    FILL_LATER_ORDER_RELEASED: releasedOrderOpened
      ? `Order ${splitOrderId || heldOrderId} released and opened for ${rxLabel}`
      : `${rxLabel} released on order ${
          splitOrderId || heldOrderId
        }; order stays held for ${stillHeldCount} more item${stillHeldCount === 1 ? '' : 's'}`,
    REFILL_ORDER_CREATED_FROM_API: `Refill order ${orderId} has been created from API`,
    REFILL_ORDER_CREATED_FROM_PORTAL: `Refill order ${orderId} has been created from Portal`,
    REFILL_ORDER_CREATED_FROM_POM: `Refill order ${orderId} has been created from POM`,
    SHIPMENT_VOIDED: 'Shipment has been voided',
    SHIPSTATION_ORDER_CREATION_FAILED: 'Shipstation order creation failed',
    SHIPSTATION_ORDER_ALREADY_EXISTS: 'Shipstation order already exists',
    SHIPSTATION_ORDER_CREATED_RECONCILED: 'Shipstation order created reconciled',
    OPEN: `Order moved back to open state`,
    SHIPMENT_NOTIFICATION_SUPPRESSED:
      'Notification suppressed due to disabled settings or order age, please check json for more details',
    SHIPMENT_PATIENT_EMAIL_NOTIFICATION_SENT: `Shipment patient email notification sent${patientEmail ? ` to ${patientEmail}` : ''}`,
    PHARM_TECH_LABEL_VIAL_SCAN: 'Pharm Tech labeling vial before sending to pharmacist',
    PHARMACIST_PRODUCT_LABEL_VERIFICATION: 'Pharmacist Product Label Verified',
    PRODUCT_SCANNED_FOR_SYRINGE: 'Product Scanned for Syringe',
    ADD_PATIENT_PARTNER: 'Patient partner added',
    UPDATE_PATIENT_PARTNER: 'Patient partner updated',
    ADD_PATIENT_PRACTICE: 'Patient practice added',
    UPDATE_PATIENT_PRACTICE: 'Patient practice updated',
    UPDATE_ORDER_ASSOCIATION: 'Order association updated',
    PRX_SHIPMENT_CREATED: 'Shipment created in PRX Shipment module',
    PRX_SHIPMENT_UPDATED: 'Shipment got updated in PRX Shipment module',
    PRX_SHIPMENT_SHIPPED: 'Shipment got shipped in PRX Shipment module',
    PRX_SHIPMENT_LABEL_CREATED: 'Shipment label got created in PRX Shipment module',
    PRX_SHIPMENT_CREATION_FAILED: 'Shipment creation failed in PRX Shipment module',
    PRX_SHIPMENT_UPDATE_FAILED: 'Shipment update failed in PRX Shipment module',
    PRX_SHIPMENT_LABEL_CREATION_FAILED: 'Shipment label creation failed in PRX Shipment module',
    PRX_SHIPMENT_CANCEL_FAILED: 'Shipment cancelation failed in PRX Shipment module',
    ADD_ON_ATTACHED: `${eventData?.addOnItemName || 'Add-on'} (Qty: ${eventData?.quantity ?? 'N/A'}) attached to order ${orderId}${eventData?.source ? ` via ${eventData.source.toLowerCase().replace(/_/g, ' ')}` : ''}`,
    ADD_ON_REVIEW: `Add-on review required for order ${orderId}${eventData?.drugName ? ` — ${eventData.drugName}${eventData?.strength ? ` ${eventData.strength}` : ''}` : ''}`,
    ADD_ON_REVIEW_RESOLVED: eventData?.skipAddOn
      ? `Add-on review resolved for order ${orderId} — add-on skipped`
      : `Add-on review resolved for order ${orderId}${eventData?.quantity ? ` — Qty: ${eventData.quantity}` : ''}`,
    ADD_ON_QUANTITY_UPDATED: `Add-on quantity updated from ${eventData?.oldQuantity ?? 'N/A'} to ${eventData?.newQuantity ?? 'N/A'}`,
    PRX_SHIPMENT_TIMEOUT: 'Shipment processing timed out in PRX Shipment module',
    PRX_SHIPMENT_EXISTS: 'Shipment already exists in PRX Shipment module',
    PRX_SHIPMENT_CREATED_RECONCILED: `Shipment got reconciled in PRX shipment for order ${orderId}`,
    SET_LINE_ITEM_SHIP_BY_DATE: `Ship by date overridden for order ${orderId}`,
    CLEAR_LINE_ITEM_SHIP_BY_DATE: `Ship by date override cleared — restored to system-calculated ship by date for order ${orderId}`,
    APPLY_URGENT_TAG: `Urgent tag applied to line item for order ${orderId}`,
    REMOVE_URGENT_TAG: `Urgent tag removed from line item for order ${orderId}`,
    NDC_OVERRIDDEN: `NDC overridden${eventData?.productId ? ` for product ${eventData.productId}` : ''}${
      eventData?.previousNdc && eventData?.newNdc
        ? ` — changed from ${eventData.previousNdc} to ${eventData.newNdc}`
        : ''
    }${eventData?.isBulk ? ' (bulk override)' : ''}`,
    PRODUCT_OVERRIDDEN: `Product overridden${
      overriddenProductId
        ? ` — ${
            previousProductId ? `changed from ${previousProductId} to ` : 'set to '
          }${overriddenProductId}`
        : ''
    }${eventData?.isBulk ? ' (bulk override)' : ''}`,
    QUANTITY_OVERRIDDEN: `Dispensed quantity overridden${
      overriddenQuantity === ''
        ? ''
        : ` — ${
            previousQuantity === '' ? 'set to ' : `changed from ${previousQuantity} to `
          }${overriddenQuantity}`
    }${eventData?.isBulk ? ' (bulk override)' : ''}`,
    SHIP_LATER_REVIEW_RESOLVED: `Ship Later review resolved`,
    SHIP_LATER_REVIEW: 'Item tagged for Ship later review',
    COMPOUNDING_REVIEW: 'Order marked for compounding review',
    COMPOUNDING_STATE_NOT_LICENSED_REVIEW:
      "Order marked for compounding review — compounding pharmacy not licensed in patient's shipping state",
    COMPOUNDING_REVIEW_RELEASED: `Compounding review released${orderId ? ` for order ${orderId}` : ''}`,
    COMPOUNDING_ORDER_SUBMITTED_WEBHOOK_RECEIVED: `Compounding order submitted webhook received from compounding pharmacy${orderId ? ` for order ${orderId}` : ''}`,
    COMPOUNDING_ORDER_ACCEPTED_WEBHOOK_RECEIVED: `Compounding order accepted webhook received from compounding pharmacy${orderId ? ` for order ${orderId}` : ''}`,
    COMPOUNDING_ORDER_SHIPPED_WEBHOOK_RECEIVED: `Compounding order shipped webhook received from compounding pharmacy${orderId ? ` for order ${orderId}` : ''}`,
    COMPOUNDING_ORDER_DELIVERED_WEBHOOK_RECEIVED: `Compounding order delivered webhook received from compounding pharmacy${orderId ? ` for order ${orderId}` : ''}`,
    COMPOUNDING_ORDER_RECALL_REQUESTED: `Order recall requested from compounding pharmacy${orderId ? ` for order ${orderId}` : ''}`,
    COMPOUNDING_ORDER_RECALLED: `Order recalled from compounding pharmacy${orderId ? ` for order ${orderId}` : ''}`,
    COMPOUNDING_ORDER_RECALL_FAILED: `Unable to recall order. Contact compounding pharmacy.${eventData?.error ? ` — ${eventData.error}` : ''}`,
    COMPOUNDING_ORDER_FORCE_RETURNED: `Order routed to the default PerfectRx workflow (manual operational override)${eventData?.reason ? ` — ${eventData.reason}` : ''}`,
    COMPOUNDING_EXTERNAL_STATUS_IGNORED: `Compounding pharmacy reported ${
      eventData?.reportedStatus ? eventData.reportedStatus.replaceAll('_', ' ') : 'a status update'
    } after the order was ${
      eventData?.previousStatus ? eventData.previousStatus.replaceAll('_', ' ') : 'returned'
    } — external update ignored`,
    CREATING_COMPOUNDING_ORDER: 'Creating compounding order',
    COMPOUNDING_ORDER_CREATED: 'Compounding order created',
    COMPOUNDING_ORDER_REPOSTED: `Compounding order reposted to the compounding pharmacy${
      eventData?.previousStatus
        ? ` — previous status: ${eventData.previousStatus.replaceAll('_', ' ')}`
        : ''
    }`,
    COMPOUNDING_ORDER_MANUALLY_SENT: `Order manually sent to central fill from the bulk queue${
      orderId ? ` for order ${orderId}` : ''
    }`,
    COMPOUNDING_ORDER_MANUALLY_DELIVERED:
      eventData?.result === 'FAILURE'
        ? `Manual delivered update failed${failureReason ? ` — ${failureReason}` : ''}`
        : `Order manually marked delivered${
            eventData?.deliveredDate ? ` on ${eventData.deliveredDate}` : ''
          }${eventData?.reason ? ` — ${eventData.reason}` : ''}`,
    ORDER_ROUTED_TO_VERIFICATION: `Order routed to verification status`,
    ORDER_ROUTED_TO_VERIFIED: `Order routed to verified status`,
    CARRIER_PICKED_UP_PARTNER_WEBHOOK_SUCCESS:
      'Carrier picked up event webhook was successfully sent to the partner',
    CARRIER_PICKED_UP_PARTNER_WEBHOOK_FAILURE:
      'Carrier picked up event webhook failed to send to the partner',
    OUT_FOR_DELIVERY_PARTNER_WEBHOOK_SUCCESS:
      'Out for delivery event webhook was successfully sent to the partner',
    OUT_FOR_DELIVERY_PARTNER_WEBHOOK_FAILURE:
      'Out for delivery event webhook failed to send to the partner',
    DELIVERED_PARTNER_WEBHOOK_SUCCESS:
      'Delivered event webhook was successfully sent to the partner',
    DELIVERED_PARTNER_WEBHOOK_FAILURE: 'Delivered event webhook failed to send to the partner',
  };

  return actionItemTypeHandler[actionItemType];
};
